<script setup lang="ts">
import { storeToRefs } from 'pinia'
import { onMounted, computed, watch, ref } from 'vue'
import LibrarySidebarMenu from '@/library/LibrarySidebarMenu.vue'
import ProgressSpinner from 'primevue/progressspinner'
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import LibraryFolderContentSection from '@/library/LibraryFolderContentSection.vue'
import SelectGroup from '@/componentlibrary/SelectGroup.vue'
import PremiumRequired from '@/components/PremiumRequired.vue'
import PremiumActionModal from '@/components/PremiumActionModal.vue'
import { capitalize } from '@/util/strings'
import { useRecentlyViewedStore } from './recently_viewed.store'
import { getAnalyticsTracker } from '@/tracking/analytics/plugin'
const recentlyViewedStore = useRecentlyViewedStore()
const { loading, loadError, selectedSubject, possibleSubjects } = storeToRefs(recentlyViewedStore)
import { useUserStore } from '@/authentication/user.store'

type ContentType = 'goals' | 'strategies' | 'resources'

const props = defineProps({
  type: {
    type: String as () => ContentType,
    required: true
  }
})

const analytics = getAnalyticsTracker()
const userStore = useUserStore()
const { isTrial, isLicenseExpired } = storeToRefs(userStore)

const openPremiumAction = ref(false)

const onSubjectFilterClick = () => {
  if (isTrial.value) {
    openPremiumAction.value = true
  }
}

const ANALYTICS_EVENTS = {
  VIEWED: {
    goals: 'Viewed Recently Viewed Goals',
    strategies: 'Viewed Recently Viewed Strategies',
    resources: 'Viewed Recently Viewed Resources'
  },
  SELECTED_FILTER: {
    goals: 'Selected Recently Viewed Goals Subject Filter',
    strategies: 'Selected Recently Viewed Strategies Subject Filter',
    resources: 'Selected Recently Viewed Resources Subject Filter'
  },
  CLEARED_FILTER: {
    goals: 'Cleared Recently Viewed Goals Subject Filter',
    strategies: 'Cleared Recently Viewed Strategies Subject Filter',
    resources: 'Cleared Recently Viewed Resources Subject Filter'
  }
}

const handleSubjectFilterSelect = (value: string) => {
  if (value !== recentlyViewedStore.previousSelectedSubject) {
    if (value === '') {
      analytics.sendEvent(ANALYTICS_EVENTS.CLEARED_FILTER[props.type])
    } else {
      analytics.sendEvent(ANALYTICS_EVENTS.SELECTED_FILTER[props.type], { filter: value })
    }
  }
  recentlyViewedStore.previousSelectedSubject = recentlyViewedStore.selectedSubject
}

const contentTypeTitle = computed(() => capitalize(props.type))
const noRecentContentMsg = computed(() =>
  `You haven't viewed any ${props.type} in the last 30 days.`
)

const loadView = () => {
  recentlyViewedStore.fetchResources(props.type)
  analytics.sendEvent(ANALYTICS_EVENTS.VIEWED[props.type])
}
onMounted(() => {
  loadView()
})

watch(
  () => props.type, () => {
    loadView()
  }
)
</script>

<template>
  <PremiumRequired v-if="isLicenseExpired" />
  <div v-else class="container">
    <div class="grid grid-cols-12 gap-4 pt-12">
      <div class="col-span-12 py-4 pl-4 pr-8 md:col-span-4 lg:col-span-3">
        <LibrarySidebarMenu />
      </div>
      <div class="col-span-12 p-4 md:col-span-8 lg:col-span-9">
        <h1 class="header-md font-medium">
          Recently Viewed: <span class="font-bold">{{ contentTypeTitle }}</span>
        </h1>
        <div v-if="loading && !loadError" class="pt-2 inline-flex items-center">
          <ProgressSpinner
            strokeWidth="8"
            class="stroke-primary-400 h-5 w-5"
          />
          <span class="ml-2">Loading resources...</span>
        </div>
        <GbInlineMessage gbTheme="grayDark" class="mt-8" v-else-if="!loading && loadError">
          <template #icon><span class="material-icons-round"> info_outline </span></template>
          We're not able to retrieve your recently viewed content. Try again later.
        </GbInlineMessage>
        <div v-else>
          <div class="grid grid-cols-1 md:grid-cols-3">
              <SelectGroup
                v-model="selectedSubject"
                :filterGroup="possibleSubjects"
                groupLabel="Subject"
                placeholder="All Subjects"
                @click="onSubjectFilterClick"
                @update:modelValue="handleSubjectFilterSelect"
              />
            </div>
          <PremiumActionModal v-model:externalOpen="openPremiumAction" />
          <div class="recently-viewed mb-9">
            <hr class="border-gray-400" />
            <h2 class="mb-8 mt-2 text-lg font-medium">Viewed in the last 30 days</h2>
            <p
                v-if="!recentlyViewedStore.hasRecentlyViewed"
                class="mb-20 mt-14 text-center italic"
              >
                {{ noRecentContentMsg }}
              </p>
            <LibraryFolderContentSection
              v-if="recentlyViewedStore.hasRecentlyViewed"
              :contentObjects="recentlyViewedStore.recentlyViewed"
              :contentTypeTitle="contentTypeTitle"
              :removableContents="false"
              :showContentTypeTitle="false"
              :showAll="true"
              :gridView="type !== 'resources'"
              :clickable="!isTrial && type !== 'resources'"
              v-bind="type === 'strategies' && { numberOfItems: 2 }"
            />
          </div>
          <div v-if="recentlyViewedStore.hasPreviouslyViewed" class="previously-viewed mb-9">
            <hr class="border-gray-400" />
            <h2 class="mb-8 mt-2 text-lg font-medium">Viewed more than 30 days ago</h2>
              <LibraryFolderContentSection
                v-if="recentlyViewedStore.hasPreviouslyViewed"
                :contentObjects="recentlyViewedStore.previouslyViewed"
                :contentTypeTitle="contentTypeTitle"
                :removableContents="false"
                :showContentTypeTitle="false"
                :showAll="true"
                :gridView="type !== 'resources'"
                :clickable="!isTrial && type !== 'resources'"
                v-bind="type === 'strategies' && { numberOfItems: 2 }"
              />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>